import { TransferProgressEvent } from '@azure/core-http';
import { IFileBlob } from 'components/controls/FileImportDialog';
import { ISaveInstruction } from 'global/Application/dataActions';
import { IDatasetExtract } from 'global/ExcelExtract/IDatasetExtract';
import JSZip from 'jszip';
import * as IXrmOptions from 'model/AttributeOptionSets.type';
import { IAccountExtended, IPicked_bcbs_sbc, IQuery_bcbs_sbc, ISystemUserExtended, Ibcbs_agencydetail_extended, Ibcbs_grouplibraryprocess_extended } from 'model/CRM.type';
import { ICheckList } from 'model/ICheckList';
import * as IXrmTypes from '../../model/CRM';
import * as FAM from "../../model/FlexibleAttributeModel";
import { trimGUIDLower } from '../Utility/TSFunctions';
import { BlobDataSource, IBlobLease, IBlobUploadResult, ISasUriProvider } from './Azure/BlobDataSource';
import { ITokenSource } from './ITokenSource';
import { NetDataSource } from './Net/NetDataSource';
import { IApiOptions } from './Xrm/IXrmDataSource';
import { XrmDataSource } from './Xrm/XrmDataSource';
import * as IXrmGlobalOptions from 'model/GlobalOptionSetStatic';
import { ICheckListData } from 'views/BlueRelationship/CheckList/ICheckListData';
import { extname } from 'path';
import { emitCustomEvent } from 'global/Application';


export interface IFetchOpenRevisionTasksResponse {
    "@odata.etag": string;
    "activitytypecode@OData.Community.Display.V1.FormattedValue": string;
    activitytypecode: string;
    subject: string;
    activityid: string;
}


export interface IAuditAttribute {
    key: string;
    value: string | Record<string, any> | null;
}

export interface IAuditFieldValueUserID {
    id: string;
    logicalName: string;
    name: string;
    keyAttributes: any[];
    rowVersion: null;
}

export interface IAuditFields {
    id: string;
    logicalName: string;
    attributes: IAuditAttribute[];
    entityState: null;
    formattedValues: IAuditAttribute[];
    relatedEntities: any[];
    rowVersion: null;
    keyAttributes: any[];
};

export interface IAuditResponseAuditDetails {
    oldValue: IAuditFields,
    newValue: IAuditFields,
    // attributes.key: objectid, userid, versionnumber, operation, createdon, auditid, attributemask, action, objecttypecode, transactionid, 
    auditRecord: IAuditFields,
    invalidNewValueAttributes: [],
    deletedAttributes: {},
    locLabelLanguageCode: number,
}

export interface IAuditResponse {
    auditDetails: IAuditResponseAuditDetails[],
    moreRecords: boolean,
    pagingCookie: string,
    totalRecordCount: number,
}


class NetSasUriProvider implements ISasUriProvider {
    private readonly nds: NetDataSource;
    constructor(nds: NetDataSource) {
        this.nds = nds;
    }
    async getSasUri(containerName: string, blobName: string): Promise<string> {
        let response = await this.nds.execute("GET", `api/SnapshotSASUri?containerName=${containerName}&blobName=${blobName}`);
        return response.uri;
    }
}

export interface ISaveResult {
    newBlobVersion?: string;
    snapshot?: IXrmTypes.bcbs_groupconfigurationsnapshot;
    data?: FAM.IGroupConfigurationData | null;
    instructions?: ISaveInstruction;
}

export interface ISnapshotInfo {
    fileName: string;
    blobVersion: string;
    json: FAM.IGroupConfigurationData | null;
}

export interface IDownloadResult {
    etag: string;
    json: FAM.IGroupConfigurationData;
}

export interface IUploadResult {
    version: string;
}

export class AppDataSource {

    private readonly tokenSource: ITokenSource;
    readonly crm: XrmDataSource;
    readonly net: NetDataSource;
    readonly blob: BlobDataSource;

    constructor(tokenSource: ITokenSource) {
        this.tokenSource = tokenSource;

        this.crm = new XrmDataSource(tokenSource);
        this.net = new NetDataSource(tokenSource);
        this.blob = new BlobDataSource(new NetSasUriProvider(this.net));
    }

    // .NET methods
    async getSnapshotVersions(blobName: string) {
        const result = await this.net.execute('GET', `api/SnapshotVersions?blobName=${blobName}`);
        return result as string[];
    }

    async b64toBlob(base64: string, type = 'application/octet-stream') {
        const t = await fetch(`data:${type};base64,${base64}`);
        return await t.blob();
    }

    async getTemplate(requestJson: string) {
        const result = await this.net.execute('POST', 'api/Excel/Build', requestJson);

        return {
            templateBlob: await this.b64toBlob(result.fileBase64),
            messages: result.messages as ({ message: string }[])
        };
    }

    async getGenerateExcelExtract(requestJson: IDatasetExtract) {
        const result = await this.net.execute('POST', 'api/Excel/GenerateExcelExtract', requestJson);

        return {
            file: await this.b64toBlob(result.fileBase64),
            messages: result.messages as ({ message: string }[])
        };
    }

    async getBenefitDetailReport(snapshotId: string, payload: string) {
        const response = await this.net.executeDurable('POST', 'api/AzureFunction/GenerateClientBenefitDetailReportDurable', {
            snapshotId: snapshotId,
            payload: payload
        });

        const blob = await this.b64toBlob(response.base64);

        return {
            blob: blob,
            fileName: response.fileName
        };
    }

    async getBenefitSummaryReport(
        templateId: string | null,
        snapshot?: {
            bcbs_groupconfigurationsnapshot: IXrmTypes.bcbs_groupconfigurationsnapshot,
            plans: FAM.IPlan[],
        },
        opportunityOption?: {
            accountId: string,
            blobName: string,
        },
        log?: boolean,
    ) {

        interface IPayload {
            templateId: string | null;
            fromOpportunityOption?: {
                accountId: string,
                blobName: string,
            };
            fromGroupConfigurationSnapshot?: {
                bcbs_groupconfigurationsnapshotid: string,
                plans: { id: string }[],
            };
            log?: boolean;
        }

        if (snapshot === undefined && opportunityOption === undefined)
            throw new Error("Invalid Argument Exception - snapshot and opportunityOption cannot both be undefined.");

        const requestPayload: IPayload = {
            templateId: templateId,
            fromOpportunityOption: opportunityOption ? opportunityOption : undefined,
            fromGroupConfigurationSnapshot: snapshot && snapshot.plans.length > 0
                ? {
                    bcbs_groupconfigurationsnapshotid: snapshot.bcbs_groupconfigurationsnapshot.bcbs_groupconfigurationsnapshotid!,
                    plans: snapshot.plans.map(t => ({ id: t.id })),
                } :
                undefined,
            log: log,
        }

        const response = await this.net.executeDurable(
            'POST',
            'api/AzureFunction/GenerateBenefitSummaryReportDurable',
            requestPayload,
        );

        const blob = await this.b64toBlob(response.base64);

        return {
            blob: blob,
            fileName: response.fileName
        };
    }





    async releaseRxOre(snapshotId: string) {
        const response = await this.net.execute('POST', 'api/AzureFunction/GenerateOptumBulkExtractReports', {
            snapshotId: snapshotId,
            releaseRx: true
        });

        return response;
    }

    async getOptumBulkExtractReports(snapshotId: string) {
        const response = await this.net.execute('POST', 'api/AzureFunction/GenerateOptumBulkExtractReports', {
            snapshotId: snapshotId,
            releaseRx: false
        });

        const blob = await this.b64toBlob(response.base64);

        return {
            blob: blob,
            fileName: response.fileName
        };
    }


    async getSnapshotCompareExtract(
        leftSnapshot: {
            bcbs_groupconfigurationsnapshotid: string,
            blobVersion?: string | null
        },
        right: {
            bcbs_groupconfigurationsnapshotid: string,
            blobVersion?: string | null
        },
        generateSinglePage: boolean,
        progressCallback?: () => void
    ) {
        const response = await this.net.executeDurable(
            'POST',
            'api/AzureFunction/GenerateSnapshotCompareExtractDurable',
            {
                leftSnapshotId: leftSnapshot.bcbs_groupconfigurationsnapshotid,
                leftBlobVersion: leftSnapshot.blobVersion,
                rightSnapshotId: right.bcbs_groupconfigurationsnapshotid,
                rightBlobVersion: right.blobVersion,
                generateSinglePage: generateSinglePage,
            },
            undefined,
            progressCallback
        );

        const blob = await this.b64toBlob(response.base64);

        return {
            blob: blob,
            fileName: response.fileName
        };
    }

    async getBenefitDetailExtract(payload: { snapshotId: string, production: 'yes' | 'no', sftp: 'yes' | 'no' }, progressCallback?: () => void) {
        const response = await this.net.executeDurable('POST', 'api/AzureFunction/GenerateBenefitDetailExtractDurable', payload, undefined, progressCallback);

        const blob = await this.b64toBlob(response.base64);

        return {
            blob: blob,
            fileName: response.fileName
        };
    }

    async getAdobeCampaignExtract(payload: { production: 'yes' | 'no', sftp: 'yes' | 'no' }, callback?: () => void) {
        const response = await this.net.executeDurable('POST', 'api/AzureFunction/GenerateAdobeCampaignExtractDurable', payload, undefined, callback);

        const blob = await this.b64toBlob(response.base64);

        return {
            blob: blob,
            fileName: response.fileName
        };
    }


    async getGenerateDataDictionaryExtract() {
        const response = await this.net.execute('POST', 'api/AzureFunction/GenerateDataDictionaryExtract');

        const blob = await this.b64toBlob(response.fileContentBase64);
        return {
            blob: blob,
            fileName: response.fileName
        };
    }

    async getGenerateSnapshotValidation(bcbs_groupconfigurationsnapshotid: string) {
        const response = await this.net.execute('POST', 'api/AzureFunction/GenerateSnapshotValidation', {
            snapshotId: bcbs_groupconfigurationsnapshotid,
        });

        return response as {
            validator: string,
            location: string,
            path: string,
            issueType: string,
            description: string,
        }[];
    }

    async getHttpStartGetSnapshotAudit(bcbs_groupconfigurationsnapshotid: string) {
        const response = await this.net.execute('GET',
            'api/AzureFunction/HttpStartGetSnapshotAudit',
            undefined,
            {
                bcbs_groupconfigurationsnapshotid: bcbs_groupconfigurationsnapshotid
            });

        return response as IAuditResponse;
    }

    async getGroupLibraryProcessByRecordId(bcbs_recordid: string) {
        return await this.crm.getRecords<Ibcbs_grouplibraryprocess_extended>(
            'bcbs_grouplibraryprocesses',
            `bcbs_recordid eq '${bcbs_recordid}'`,
            undefined,
            { includeAnnotations: true }
        );
    }


    async updateGroupLibraryProcess(bcbs_grouplibraryprocessid: string, data: any) {
        return await this.crm.updateAndReturn('bcbs_grouplibraryprocesses', bcbs_grouplibraryprocessid, data);
    }

    // CRM methods

    async getConfigSegments(): Promise<IXrmTypes.bcbs_config_segment[]> {
        return await this.crm.query(`bcbs_config_segments`);
    };

    async getConfigCategories(): Promise<IXrmTypes.bcbs_config_category[]> {
        return await this.crm.query(`bcbs_config_categories`);
    };

    async getConfigTabs(): Promise<IXrmTypes.bcbs_config_tab[]> {
        return await this.crm.query(`bcbs_config_tabs`);
    };

    async getConfigSections(): Promise<IXrmTypes.bcbs_config_section[]> {
        return await this.crm.query(`bcbs_config_sections`);
    };

    async getConfigAttributes(): Promise<IXrmTypes.bcbs_config_attribute[]> {
        return await this.crm.query(`bcbs_config_attributes`);
    };

    async getConfigAttributeRows(): Promise<IXrmTypes.bcbs_config_row[]> {
        return await this.crm.query(`bcbs_config_rows`);
    };

    async getConfigMasterAttributes(): Promise<IXrmTypes.bcbs_config_masterattribute[]> {
        return await this.crm.query(`bcbs_config_masterattributes`);
    };

    async getConfigOptionSets(): Promise<IXrmTypes.bcbs_config_optionset[]> {
        return await this.crm.query(`bcbs_config_optionsets`);
    };

    async getConfigOptionSetItems(): Promise<IXrmTypes.bcbs_config_optionsetitem[]> {
        return await this.crm.query(`bcbs_config_optionsetitems`);
    };

    async getCopayCoinOptions(): Promise<IXrmTypes.bcbs_copaycoinoption[]> {
        const path = `bcbs_config_categories?$select=bcbs_name,bcbs_config_categoryid&$expand=bcbs_CopayCoinOption_Config_Category($select=bcbs_copaycoinsurancetypecode,bcbs_copaycoinoptionid,bcbs_name,bcbs_iscurrencyonly, bcbs_ispercentageonly, statuscode)`;

        var response = await this.crm.query(path, {
            additionalHeaders: {
                'x-gl-cache': 'yes',
                'x-gl-cache-revalidate-delay': '20000',
            }
        });

        return response;
    };

    async getConfigTierSets(): Promise<IXrmTypes.bcbs_config_tierset[]> {
        return await this.crm.query(`bcbs_config_tiersets`);
    };

    async getConfigTierLevels(): Promise<IXrmTypes.bcbs_config_tierlevel[]> {
        return await this.crm.query(`bcbs_config_tierlevels`);
    };

    async getBCCAdmins(): Promise<IXrmTypes.bcbs_bccadmin[]> {

        const path = `bcbs_bccadmins`

        return await this.crm.query(path, {
            additionalHeaders: {
                'x-gl-cache': 'yes',
                'x-gl-cache-revalidate-delay': '20000',
            },
        });
    };

    async getGlobalOptionSetDefinitions(): Promise<IXrmTypes.OptionSetMetadata[]> {

        const names: IXrmGlobalOptions.TOptionSetNames[] = [
            'bcbs_fundingtypes',
            'bcbs_groupconfigurationchangetypecode',
            'bcbs_erisacode',
            'bcbs_lob',
            'bcbs_grandfatheredcode',
            'bcbs_idcardorderingoptioncode',
        ]

        if (names && names.length > 0) {

            const resultPromise = new Promise((resolve, reject) => {

                const options = names.map(name =>
                    this.crm.execute('GET', `GlobalOptionSetDefinitions(Name='${name}')`)
                );

                Promise.all(options).then(values => {
                    resolve(values);
                });

            }) as Promise<IXrmTypes.OptionSetMetadata[]>;

            const result = await resultPromise;
            return result;
        }
        else {
            return await this.crm.query(`GlobalOptionSetDefinitions`);
        }
    };

    async getAccount(id: string, fields?: string, options?: IApiOptions): Promise<IAccountExtended> {
        return await this.crm.getRecord('accounts', trimGUIDLower(id), fields, { ...options, includeAnnotations: true });
    };

    async getContact(id: string): Promise<IXrmTypes.contact> {
        // TODO query only fields necessary
        return await this.crm.getRecord('contacts', trimGUIDLower(id), undefined, { includeAnnotations: true });
    };

    async getBcbsCarrier(id: string): Promise<IXrmTypes.contact> {
        // TODO query only fields necessary
        return await this.crm.getRecord('bcbs_carriers', trimGUIDLower(id), undefined, { includeAnnotations: true });
    };


    async getTask(id: string): Promise<IXrmTypes.contact> {
        // TODO query only fields necessary
        return await this.crm.getRecord('tasks', trimGUIDLower(id), undefined, { includeAnnotations: true });
    };

    async getSystemUser(id: string): Promise<ISystemUserExtended> {
        // TODO query only fields necessary
        return await this.crm.getRecord('systemusers', trimGUIDLower(id), 'bcbs_repcode', { includeAnnotations: true });
    };

    async getSystemUsers() {
        // TODO query only fields necessary
        return await this.crm.getRecords<ISystemUserExtended>(
            'systemusers',
            undefined,
            'systemuserid, fullname, domainname'
        );
    };


    async getAgencyDetailByGroup(id: string): Promise<IXrmTypes.bcbs_agencydetail[]> {
        // TODO query only fields necessary
        const agencyDetails = await this.crm.query(`bcbs_agencydetails?$filter=_bcbs_groupid_value eq '${trimGUIDLower(id)}'`, { includeAnnotations: true }) as Ibcbs_agencydetail_extended[];

        for await (const eachAgency of agencyDetails) {

            if (eachAgency._bcbs_agentid_value) {
                const contact = await this.getContact(eachAgency._bcbs_agentid_value);
                eachAgency.agentContact = contact;

            }

            if (eachAgency._bcbs_agencyid_value) {
                const account = await this.getAccount(eachAgency._bcbs_agencyid_value);
                eachAgency.agentAccount = account;
            }
        };

        return agencyDetails;
    };

    async getOpportunity(id: string): Promise<IXrmTypes.opportunity> {
        // TODO query only fields necessary
        return await this.crm.getRecord('opportunities', trimGUIDLower(id), undefined, { includeAnnotations: true });
    };

    async getContactsForAccount(id: string, lookupFields?: (keyof IXrmTypes.contact)[]): Promise<IXrmTypes.contact[]> {
        // TODO query only fields necessary
        return await this.crm.getRecordLookupProperty(
            'accounts',
            trimGUIDLower(id),
            'contact_customer_accounts',
            lookupFields ? lookupFields.join(',') : undefined)
    };

    async getGroupConfigurationSnapshotsForAccount(id: string): Promise<IXrmTypes.bcbs_groupconfigurationsnapshot[]> {
        return await this.crm.query(`bcbs_groupconfigurationsnapshots?$filter=_bcbs_groupid_value eq '${trimGUIDLower(id)}'`, { includeAnnotations: true });
    }

    async getQuoteRequestsForOpportunity(id: string): Promise<IXrmTypes.bcbs_quoterequest[]> {
        return await this.crm.query(`bcbs_quoterequests?$filter=_bcbs_opportunityid_value eq '${trimGUIDLower(id)}'`, { includeAnnotations: true });
    }


    async getGroupConfigurationSnapshot(id: string, fields?: string, options?: IApiOptions): Promise<IXrmTypes.bcbs_groupconfigurationsnapshot> {
        return await this.crm.getRecord('bcbs_groupconfigurationsnapshots', trimGUIDLower(id), fields, options);
    }

    async createGroupConfigurationSnapshot(data: any): Promise<IXrmTypes.bcbs_groupconfigurationsnapshot> {
        return await this.crm.createAndReturn('bcbs_groupconfigurationsnapshots', data);
    }

    async createAndReturnEnrollmentDetail(data: any): Promise<IXrmTypes.bcbs_enrollmentdetails> {
        return await this.crm.createAndReturn('bcbs_enrollmentdetailses', data);
    }

    async getEnrollmentDetail(id: string) {
        return await this.crm.getRecord('bcbs_enrollmentdetailses', id) as IXrmTypes.bcbs_enrollmentdetails;
    }

    async getPublishedRelease() {
        const result = await this.crm.getRecords<IXrmTypes.bcbs_config_release>(
            'bcbs_config_releases',
            `statuscode eq ${IXrmOptions.bcbs_config_release.statuscode['Published(2)'].value}`
        );


        if (result?.value?.length !== 1) {
            throw new Error("Unexpected release encountered while loading the publish release.")
        }

        const bcbs_config_release = result?.value?.[0] as IXrmTypes.bcbs_config_release;

        console.log("[apii] AppDataSource.getPublishedRelease", {
            bcbs_name: bcbs_config_release.bcbs_name,
            bcbs_versionnumber: bcbs_config_release.bcbs_versionnumber,
            id: bcbs_config_release.bcbs_config_releaseid
        });

        return bcbs_config_release
    }

    async getRelease(bcbs_config_releaseid: string) {
        if (!bcbs_config_releaseid)
            throw new Error("Invalid Argument Exception - missing bcbs_config_releaseid");

        const bcbs_config_release = await this.crm.getRecord('bcbs_config_releases', bcbs_config_releaseid) as IXrmTypes.bcbs_config_release;

        console.log("[apii] AppDataSource.getRelease", {
            bcbs_name: bcbs_config_release.bcbs_name,
            bcbs_versionnumber: bcbs_config_release.bcbs_versionnumber,
            id: bcbs_config_release.bcbs_config_releaseid
        });

        return bcbs_config_release;

    }

    async updateEnrollmentDetail(id: string, data: any) {
        return await this.crm.updateAndReturn('bcbs_enrollmentdetailses', id, data);
    }

    async createGroupConfigurationSnapshotEvent(data: any): Promise<IXrmTypes.bcbs_groupconfigurationsnapshotevent> {
        return await this.crm.createAndReturn('bcbs_groupconfigurationsnapshotevents', data);
    }

    async updateConfigurationSnapshotEvent(data: Partial<IXrmTypes.bcbs_groupconfigurationsnapshotevent>) {
        const id = data.bcbs_groupconfigurationsnapshoteventid!;

        return await this.crm.updateAndReturn('bcbs_groupconfigurationsnapshotevents', id, data, []);
    }

    // TODO move these to AppDataSource
    async getConfigAttributeNonStandardCategories(): Promise<IXrmTypes.bcbs_config_attribute[]> {
        return await this.crm.query(`bcbs_config_attribute_bcbs_config_categset`);
    };

    async getConfigAttributeNonStandardSections(): Promise<IXrmTypes.bcbs_config_attribute[]> {
        return await this.crm.query(`bcbs_config_attribute_bcbs_config_sectiset`);
    };

    async updateGroupConfigurationSnapshot(id: string, data: any, version?: number): Promise<IXrmTypes.bcbs_groupconfigurationsnapshot> {
        return await this.crm.updateAndReturn('bcbs_groupconfigurationsnapshots', trimGUIDLower(id), data, [], version);
    }


    async getSBCConfigs(): Promise<IXrmTypes.bcbs_sbcconfig[]> {
        return await this.crm.query('bcbs_sbcconfigs');
    }

    async createSBCLog(data: any): Promise<IXrmTypes.bcbs_sbclog> {
        return await this.crm.createAndReturn('bcbs_sbclogs', data);
    }

    async createSBC(data: any): Promise<IXrmTypes.bcbs_sbc> {
        return await this.crm.createAndReturn('bcbs_sbcs', data);
    }

    async getSBC(snapshotId: string): Promise<IPicked_bcbs_sbc[]> {
        return await this.crm.query(`bcbs_sbcs?$select=bcbs_name,bcbs_planid, bcbs_sbctype, statuscode&$filter=_bcbs_group_name_value eq '${trimGUIDLower(snapshotId)}'`);//need to return bcbs_sbctype as well
    }

    async updateSBC(id: string, data: any): Promise<IXrmTypes.bcbs_sbc> {
        return await this.crm.updateAndReturn('bcbs_sbcs', id, data);
    }


    async createBNC(data: any): Promise<IXrmTypes.bcbs_bncreport> {
        return await this.crm.createAndReturn('bcbs_bncreports', data);
    }

    async getBNC(snapshotId: string): Promise<Pick<IXrmTypes.bcbs_bncreport, 'bcbs_plan_type' | 'bcbs_bncreportid'>[]> {
        return await this.crm.query(`bcbs_bncreports?$select=bcbs_planid&$filter=_bcbs_group_name_value eq '${trimGUIDLower(snapshotId)}'`);
    }

    async updateBNC(id: string, data: any): Promise<IXrmTypes.bcbs_bncreport> {
        return await this.crm.updateAndReturn('bcbs_bncreports', id, data);
    }

    async createScheduleA(data: any): Promise<IXrmTypes.bcbs_schedulea> {
        return await this.crm.createAndReturn('bcbs_scheduleas', data);
    }

    async updateScheduleA(id: string, data: any): Promise<IXrmTypes.bcbs_schedulea> {
        return await this.crm.updateAndReturn('bcbs_scheduleas', id, data);
    }

    async searchAccounts(term: string, fields: string[] = ['accountid', 'name']): Promise<IXrmTypes.account[]> {
        // TODO swap with a call to Relevance Search API
        return await this.crm.query(`accounts?$filter=contains(name, '${term}')&$select=${fields?.join(',')}`);
    }

    private async deleteGroupConfigurationSnapshotField(snapshotId: string): Promise<IXrmTypes.bcbs_groupconfigurationsnapshotevent> {
        return await this.crm.deleteFileAttribute('bcbs_groupconfigurationsnapshots', snapshotId, 'bcbs_snapshot');
    }

    async downloadGroupConfigurationSnapshotFromCrm(id: string) {
        let { blob, fileName } = await this.crm.downloadFileAttributeBlob('bcbs_groupconfigurationsnapshots', id, 'bcbs_snapshot');

        const useZip = fileName?.endsWith('.zip') || fileName?.endsWith('.ZIP') || fileName?.endsWith('.Zip');

        if (useZip) {
            const zip = new JSZip();
            const contents = await zip.loadAsync(blob, {});
            const files = Object.keys(contents.files);
            if (files.length !== 1)
                throw new Error("Unexpected archive layout.");

            const json = await zip.file(files[0])?.async('string');
            return JSON.parse(json!) as FAM.IGroupConfigurationData;
        }
        else {

            //@ts-ignore
            const dataString: string = await new Promise((res) => {
                // FileReader I think always assumes UTF8
                const reader = new FileReader();
                reader.onload = () => res(reader.result as string);
                reader.readAsText(blob);
            });

            return JSON.parse(dataString) as FAM.IGroupConfigurationData;
        }
    }

    async downloadConfig(id: string) {
        console.log("[apii] AppDataSource.downloadConfig", id);

        const rawData = await this.crm.downloadFileAttributeBlob('bcbs_config_releases', id, 'bcbs_solution');
        const text = await rawData.blob.text()
        return JSON.parse(text) as FAM.IGroupConfigurationConfig;
    }

    private async convertObjectToZipBlob(data: any): Promise<Blob> {
        const json = JSON.stringify(data);
        const zip = new JSZip();
        zip.file('snapshot.json', json);
        return await zip.generateAsync({ type: 'blob', compressionOptions: { level: 9 }, compression: 'DEFLATE' });
    }

    private async convertZipBlobToObject(blob: Blob): Promise<any> {
        const zip = new JSZip();
        const contents = await zip.loadAsync(blob, {});
        const files = Object.keys(contents.files);
        if (files.length !== 1)
            throw new Error("Unexpected archive layout.");

        const json = await zip.file(files[0])?.async('string');
        return JSON.parse(json!);
    }

    async downloadBlob(fileName: string, versionId?: string | null): Promise<IDownloadResult> {
        let blob = await this.blob.downloadBlob(fileName, versionId);
        let json = await this.convertZipBlobToObject(blob.blob);

        return {
            json: json,
            etag: blob.etag!
        };
    }

    async downloadGroupConfigurationSnapshot(snapshot: IXrmTypes.bcbs_groupconfigurationsnapshot, version?: string | null) {
        // is there a snapshot file? If not, generate a file name
        let fileName = snapshot.bcbs_filename;
        if (!fileName)
            fileName = this.generateFileNameFromSnapshot(snapshot);

        // first, try to download the file from BLOB
        let result: IDownloadResult | null = null;
        try {
            result = await this.downloadBlob(fileName, version);
        } catch (error: any) {
            // there's a lot of errors that could be thrown here, but I'm checking
            // for one specific condition for compatibility: where the filename on
            // the snapshot is empty and the blob wasn't found - this indicates that
            // the snapshot is still using CRM for storage instead of BLOB and we need
            // to convert it over
            if (!snapshot.bcbs_filename && error.statusCode === 404) {
                // for now though, I think we should try to back track and see if CRM has it
                // for compatibility
                if (snapshot.bcbs_snapshot_name) {
                    try {
                        let file = await this.downloadGroupConfigurationSnapshotFromCrm(snapshot.bcbs_groupconfigurationsnapshotid!);
                        result = {
                            json: file,
                            etag: ''
                        };
                    } catch (error: any) {
                        throw error;
                    }
                }
                // if the file attribute file name field is empty, then
                // we just do blank
            } else {
                // otherwise throw an exception
                throw error;
            }
        }

        return result;

    }



    generateFileNameFromSnapshot(snapshot: IXrmTypes.bcbs_groupconfigurationsnapshot): string {
        let createDate = new Date(snapshot.createdon!);
        let year = createDate.getUTCFullYear();
        let month = (createDate.getUTCMonth() + 1) + "";
        let date = (createDate.getUTCDate()) + "";

        if (month.length === 1)
            month = "0" + month;

        if (date.length === 1)
            date = "0" + date;

        let id = trimGUIDLower(snapshot.bcbs_groupconfigurationsnapshotid!);

        // example: 2022/04/31/789ABCF3-5C3D-4D4D-ACDE-891D0CE2151E.json.zip
        return `${year}/${month}/${date}/${id}.json.zip`;
    }


    async save(
        saveInfo: ISnapshotInfo,
        snapshotRecordChanges: IXrmTypes.bcbs_groupconfigurationsnapshot,
        snapshotRecordVersion?: number
    ): Promise<ISaveResult> {
        debugger
        const savingJson = saveInfo.json !== null

        // acquire a lease on the BLOB first
        // NOTE: we can only get a lease on BLObs that exist, so we check the blob version
        // to determine if we can lease it or not. If there are two clients trying to create
        // the same blob at the same time, azure storage does guarentee atomic save if we
        // provide the if-none-match header.  This will make sure that if one client saves
        // first, then continues, the other will fail before it gets any further
        let lease: IBlobLease | null = null;

        if (savingJson && saveInfo.blobVersion)
            lease = await this.blob.acquireBlobLease(saveInfo.fileName, 60);

        try {
            // convert JSON to blob
            let blobUploadResult: IBlobUploadResult | null = null
            if (savingJson) {
                const blob = await this.convertObjectToZipBlob(saveInfo.json);

                // once we have the lease, upload the new snapshot
                blobUploadResult = await this.blob.uploadBlob(
                    {
                        blobName: saveInfo.fileName,
                        etag: saveInfo.blobVersion,
                        blob: blob
                    },
                    undefined,
                    lease?.leaseId
                );
            }

            console.log("[appi] AppDataSource.save saveInfo", saveInfo);

            // renew the lease since, at maximum, we only get 60 seconds
            if (lease)
                await lease.renew();

            // if we change the JSON BLOB, then update the record's blob version
            // this will ensure an audit trail of user->version
            if (blobUploadResult)
                snapshotRecordChanges.bcbs_blobversion = 'V' + blobUploadResult.versionId;

            // now save the snapshot record data (assigning the name)
            let snapshotRecord = await this.updateGroupConfigurationSnapshot(snapshotRecordChanges.bcbs_groupconfigurationsnapshotid!, snapshotRecordChanges, snapshotRecordVersion);

            console.log("[appi] AppDataSource.save snapshotRecordChanges", snapshotRecordChanges);

            // query the latest state of the snapshot record
            snapshotRecord = await this.getGroupConfigurationSnapshot(snapshotRecordChanges.bcbs_groupconfigurationsnapshotid!, undefined, { includeAnnotations: true });

            // clear file attribute field
            // we do this make sure we don't leave old
            // JSON around (for storage and consistency)
            if (snapshotRecord.bcbs_snapshot_name)
                await this.deleteGroupConfigurationSnapshotField(snapshotRecord.bcbs_groupconfigurationsnapshotid!);

            // NOTE: the etag has to be used here because that's how the
            // if-match headers work, the version id is used for getting
            // a specific version of the blob
            return {
                newBlobVersion: blobUploadResult?.etag ?? undefined,
                snapshot: snapshotRecord,
                data: saveInfo.json
            };
        }
        // catch (error) {
        //     // lets create a new one is payload is not providing one
        //     emitCustomEvent({
        //         eventType: 'globalException',
        //         details: error
        //     });

        // }
        finally {


            // release the lease
            if (lease)
                await lease.release();
        }
    }

    async createProcesses(result: Partial<IXrmTypes.bcbs_grouplibraryprocess>) {

        await this.crm.createProcess({
            "bcbs_retriesremaining": result.bcbs_retriesremaining ?? 10,
            "bcbs_startedon": result.bcbs_startedon ?? new Date().toISOString(),
            "bcbs_processstate": result.bcbs_processstate ?? IXrmOptions.bcbs_grouplibraryprocess.bcbs_processstate['Started(808900000)'].value,
            "bcbs_method": result.bcbs_method ?? "GET",
            "bcbs_recordentitylogicalname": result.bcbs_recordentitylogicalname ?? "bcbs_groupconfigurationsnapshot",
            "bcbs_recordid": result.bcbs_recordid,
            "bcbs_triggerreason": result.bcbs_triggerreason ?? "bcbs_gsrecordgenerationurl",
            "bcbs_name": result.bcbs_name
        });
    }

    /*
    Distinct statecode,statuscode from task:

    statecode, statecodename, statuscode, statuscodename
    0        , Open         , 2         , Not Assigned
    0        , Open         , 3         , In Progress
    0        , Open         , 4         , Pending Issue
    0        , Open         , 7         , Blocked
    0        , Open         , 808900000 , Assigned
    0        , Open         , 808900001 , Pending Billing Cycle
    1        , Completed    , 5         , Completed
    2        , Canceled     , 6         , Canceled
    2        , Canceled     , 808900002 , No Action Required

    */
    async revisionTaskUpdate(activityId: string, data: any) {
        const task = await this.crm.getRecord<any>('tasks', activityId, "statuscode,statecode");

        // if activity is closed, only sysadmins are capable of updating
        // we need to open, update, close again
        if (task.statecode === 1 || task.statecode === 2) {

            // open
            await this.crm.updateAndReturn('tasks',
                activityId,
                {
                    statecode: 0,
                    statuscode: 3,
                }
            );

            // update and close back
            return await this.crm.updateAndReturn('tasks',
                activityId,
                {
                    ...data,
                    statecode: task.statecode,
                    statuscode: task.statuscode,
                }
            );

        }
        else {
            return await this.crm.updateAndReturn('tasks', activityId, data);
        }
    }



    generateEMChecklistFilename(snapshot: IXrmTypes.bcbs_groupconfigurationsnapshot, emCheckList: IXrmTypes.bcbs_emchecklist): string {
        return `account/${snapshot._bcbs_groupid_value}/snapshot/${snapshot.bcbs_groupconfigurationsnapshotid}/checklist/${emCheckList.bcbs_planid}/checklist.json.zip`;
    }

    async create_bcbs_emchecklist(result: IXrmTypes.bcbs_emchecklist) {
        const cloned = structuredClone(result);

        if (cloned._bcbs_groupconfigurationsnapshotid_value)
            (cloned as any)["bcbs_GroupConfigurationSnapshotid@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${cloned._bcbs_groupconfigurationsnapshotid_value})`;

        delete cloned._bcbs_groupconfigurationsnapshotid_value;
        return await this.crm.create('bcbs_emchecklists', cloned);
    }

    async save_bcbs_opportunitybenefits(result: IXrmTypes.bcbs_opportunitybenefit, updateGuid?: string) {

        type TODataBinder<T> = Partial<Record<keyof T, { oDataPropertyBind: string, oDataPropertyPath: string }>>

        const cloned = structuredClone(result) as any;

        const mapping: TODataBinder<typeof result> = {
            "_bcbs_config_attributeid_value": { oDataPropertyBind: "bcbs_Config_Attributeid@odata.bind", oDataPropertyPath: "/bcbs_config_attributes" },
            "_bcbs_config_masterattributeid_value": { oDataPropertyBind: "bcbs_Config_MasterAttributeid@odata.bind", oDataPropertyPath: "/bcbs_config_masterattributes" },
            "_bcbs_opportunityoptionid_value": { oDataPropertyBind: "bcbs_OpportunityOptionid@odata.bind", oDataPropertyPath: "/bcbs_opportunityoptions" },
            "_bcbs_opportunityproduct_value": { oDataPropertyBind: "bcbs_OpportunityProduct@odata.bind", oDataPropertyPath: "/bcbs_opportunityproducts" },
        }

        // WebAPI to the odata bind
        // TODO extract to a function
        for (const key in mapping) {
            const value = cloned[key];
            const { oDataPropertyPath, oDataPropertyBind } = mapping[key as keyof typeof mapping]!;

            if (cloned[key])
                cloned[oDataPropertyBind] = `${oDataPropertyPath}(${value})`;

            delete cloned[key];
        }

        if (result._owningteam_value) {
            cloned["ownerid@odata.bind"] = `/teams(${result._owningteam_value})`;
            delete cloned._owningteam_value;
        }

        if (result._owninguser_value) {
            cloned["ownerid@odata.bind"] = `/systemusers(${result._owningteam_value})`;
            delete cloned._owninguser_value;
        }

        if (updateGuid)
            return await this.crm.update('bcbs_opportunitybenefits', updateGuid, cloned);

        return await this.crm.createAndReturn('bcbs_opportunitybenefits', cloned);
    }

    async save_bcbs_opportunityoption(result: IXrmTypes.bcbs_opportunityoption, updateGuid?: string) {

        type TODataBinder<T> = Partial<Record<keyof T, { oDataPropertyBind: string, oDataPropertyPath: string }>>

        const cloned = structuredClone(result) as any;

        const mapping: TODataBinder<typeof result> = {
            _bcbs_opportunityproductid_value: { oDataPropertyBind: "bcbs_opportunityproductid@odata.bind", oDataPropertyPath: "/bcbs_opportunityproducts" },
        }

        // WebAPI to the odata bind
        // TODO extract to a function
        for (const key in mapping) {
            const value = cloned[key];
            const { oDataPropertyPath, oDataPropertyBind } = mapping[key as keyof typeof mapping]!;

            if (cloned[key])
                cloned[oDataPropertyBind] = `${oDataPropertyPath}(${value})`;

            delete cloned[key];
        }

        if (result._owningteam_value) {
            cloned["ownerid@odata.bind"] = `/teams(${result._owningteam_value})`;
            delete cloned._owningteam_value;
        }

        if (result._owninguser_value) {
            cloned["ownerid@odata.bind"] = `/systemusers(${result._owningteam_value})`;
            delete cloned._owninguser_value;
        }


        if (updateGuid)
            return await this.crm.update('bcbs_opportunityoptions', updateGuid, cloned);

        return await this.crm.createAndReturn('bcbs_opportunityoptions', cloned);
    }


    async getOpportunityProductAndOptionsAndBenefits(bcbs_opportunityproductid: string) {

        const bcbs_opportunityoptionsPromise = this.crm.getRecords<IXrmTypes.bcbs_opportunityoption>(
            'bcbs_opportunityoptions',
            `_bcbs_opportunityproductid_value eq ${trimGUIDLower(bcbs_opportunityproductid)}`,
            undefined,
            { includeAnnotations: false },
            'createdon asc'
        );

        const bcbs_opportunityproduct = await this.crm.getRecord<IXrmTypes.bcbs_opportunityproduct>(
            'bcbs_opportunityproducts',
            trimGUIDLower(bcbs_opportunityproductid),
            undefined,
            { includeAnnotations: false }
        );

        const bcbs_quoterequest = await this.crm.getRecord<IXrmTypes.bcbs_quoterequest>(
            'bcbs_quoterequests',
            bcbs_opportunityproduct._bcbs_quoterequestid_value!
        );

        const opportunityPromise = this.getOpportunity(bcbs_quoterequest._bcbs_opportunityid_value!);

        const bcbs_opportunityoptions = await bcbs_opportunityoptionsPromise;

        const bcbs_opportunitybenefitPromises = bcbs_opportunityoptions.value.map(async (bcbs_opportunityoption) => {

            const bcbs_opportunitybenefits = await this.crm.getRecords<IXrmTypes.bcbs_opportunitybenefit>(
                'bcbs_opportunitybenefits',
                `_bcbs_opportunityoptionid_value eq ${trimGUIDLower(bcbs_opportunityoption.bcbs_opportunityoptionid!)}`,
                '_bcbs_opportunityoptionid_value, bcbs_value, bcbs_friendlyvalue, _bcbs_config_masterattributeid_value, _bcbs_config_attributeid_value',
                {
                    includeAnnotations: false
                }
            );

            return {
                bcbs_opportunityoption: bcbs_opportunityoption,
                bcbs_opportunitybenefits: bcbs_opportunitybenefits.value,
                bcbs_quoterequest: bcbs_quoterequest
            };
        });

        const bcbs_opportunitybenefit = await Promise.all(bcbs_opportunitybenefitPromises);
        const opportunity = await opportunityPromise;

        return {
            bcbs_opportunityproduct,
            bcbs_quoterequest,
            bcbs_opportunitybenefit,
            opportunity,
        };
    }

    async Transpose() {

        debugger

        function transposeObject(rows: Record<string, any>[], instanceRow: Record<string, any>) {

            const keys = Object.keys(instanceRow);
            const transposedObj: Record<string, any> = {};

            keys.forEach((key) => {
                rows.forEach((item, index) => {
                    transposedObj[key] = item[key];
                });
            });

            return transposedObj;
        }

        function transformArrayToObject(names: string[], obj: any): any {
            const result: any[] = [];

            Object.keys(obj).forEach((key) => {
                const item = obj[key];
                const row: any = {};

                names.forEach((name, index) => {
                    row[name] = item[index];
                });

                result.push(row);
            })

            return result;
        }

        const rows = [
            { name: "NameCol0Row1", field1: "fiedl1Col1Row1", field2: "field2Col2Row1", field3: "field3Col3Row1" },
            { name: "NameCol0Row2", field1: "fiedl1Col1Row2", field2: "field2Col2Row2", field3: "field3Col3Row2" },
            { name: "NameCol0Row3", field1: "fiedl1Col1Row3", field2: "field2Col2Row3", field3: "field3Col3Row3" },
        ];

        debugger;
        const transposedRows = transposeObject(rows, rows[0]);

        const names = ["Bango", "Dooran", "Tera"];

        const transposedX = transformArrayToObject(names, transposedRows);

        return transposedX;
    }



    async get_bcbs_emchecklist(id: string): Promise<IXrmTypes.bcbs_emchecklist> {
        return await this.crm.getRecord('bcbs_emchecklists', trimGUIDLower(id), undefined, { includeAnnotations: true });
    };


    async update_bcbs_emchecklist(id: string, data: any, version?: number): Promise<IXrmTypes.bcbs_groupconfigurationsnapshot> {
        return await this.crm.updateAndReturn('bcbs_emchecklists', trimGUIDLower(id), data, undefined, version);
    }

    async getChecklistsBySnapshotId(snapshotId: string) {
        return await this.crm.getRecords<IXrmTypes.bcbs_emchecklist>(
            'bcbs_emchecklists',
            `(_bcbs_groupconfigurationsnapshotid_value eq ${snapshotId})`,
            undefined,
            {
                includeAnnotations: true
            }
        );
    }

    async attachEMChecklistPDF(
        blob: Blob,
        blobName: string,
        onProgress: (progress: TransferProgressEvent) => void
    ) {
        const blobUploadResult = await this.blob.uploadBlob(
            {
                blobName: blobName,
                blob: blob,
                ignoreIfNoneMatch: true,
            },
            undefined,
            undefined,
            onProgress,
            "generated"
        );
        return blobUploadResult;
    }

    async loadEMChecklistPDF(
        blob: IFileBlob,
        blobName: string,
        onProgress: (progress: TransferProgressEvent) => void
    ) {
        const blobUploadResult = await this.blob.uploadBlob(
            {
                blobName: blob.name,
                blob: blob,
            },
            undefined,
            undefined,
            onProgress,
            "generated"
        );
        return blobUploadResult;
    }

    async get_bcbs_emchecklist_blob(
        bcbs_emchecklist: IXrmTypes.bcbs_emchecklist,
        withVersion?: string
    ) {
        // is there a snapshot file? If not, generate a file name
        let fileName = bcbs_emchecklist.bcbs_filename;

        if (!fileName)
            throw new Error("bcbs_emchecklist.bcbs_filename is not valid!");

        let result: ICheckList | null = null;

        try {

            let blob = await this.blob.downloadBlob(fileName, withVersion, "generated");
            let json = await this.convertZipBlobToObject(blob.blob);

            result = {
                bcbs_emchecklist: bcbs_emchecklist,
                blobEtag: blob.etag!,
                blob: json,
                versionNumber: bcbs_emchecklist.versionnumber,
            }

            return result;

        } catch (error: any) {
            throw error;
        }
    }


    async save_Checklist_blob(checklist: ICheckList, bcbs_emchecklist_changes?: Partial<IXrmTypes.bcbs_emchecklist>) {
        let lease: IBlobLease | null = null;
        let uploadResult: IBlobUploadResult | null = null

        try {

            if (checklist.bcbs_emchecklist.bcbs_blobversion)
                lease = await this.blob.acquireBlobLease(checklist.bcbs_emchecklist.bcbs_filename!, 60, "generated");

            const blobZipped = await this.convertObjectToZipBlob(checklist.blob);

            uploadResult = await this.blob.uploadBlob(
                {
                    blobName: checklist.bcbs_emchecklist.bcbs_filename!,
                    etag: checklist.blobEtag ?? '',
                    blob: blobZipped
                },
                undefined,
                lease?.leaseId,
                undefined,
                "generated"
            );

            if (!uploadResult)
                throw new Error("Uploading blob failed!");


            let changes: Partial<IXrmTypes.bcbs_emchecklist> | null = {
                ...bcbs_emchecklist_changes,
                bcbs_blobversion: 'V' + uploadResult.versionId,
                bcbs_emchecklistid: checklist.bcbs_emchecklist.bcbs_emchecklistid,
            }

            // making state code complient to status code
            if (changes.statuscode)
                if (
                    changes.statuscode === IXrmOptions.bcbs_emchecklist.statuscode['Approved(4)'].value ||
                    changes.statuscode === IXrmOptions.bcbs_emchecklist.statuscode['Cancelled(5)'].value
                ) {
                    if (changes.statecode !== IXrmOptions.bcbs_emchecklist.statecode['Inactive(1)'].value)
                        changes.statecode = IXrmOptions.bcbs_emchecklist.statecode['Inactive(1)'].value
                }
                else if (changes.statecode !== IXrmOptions.bcbs_emchecklist.statecode['Active(0)'].value) {
                    changes.statecode = IXrmOptions.bcbs_emchecklist.statecode['Active(0)'].value
                }



            await this.update_bcbs_emchecklist(
                checklist.bcbs_emchecklist.bcbs_emchecklistid!,
                changes,
                checklist.bcbs_emchecklist?.versionnumber,
            );

            const bcbs_emchecklist_reloaded = await this.get_bcbs_emchecklist(checklist.bcbs_emchecklist.bcbs_emchecklistid!);

            const ret: ICheckList = {
                bcbs_emchecklist: bcbs_emchecklist_reloaded,
                blobEtag: uploadResult.etag,
                blob: checklist.blob,
                versionNumber: bcbs_emchecklist_reloaded.versionnumber,
            };

            return ret;

        } finally {

            // release the lease
            if (lease)
                await lease.release();
        }
    }

    async bcbs_sbcs_forward(accountId: string, planId: string) {
        // const sbcs = await this.crm.query(`bcbs_sbcs?$select=bcbs_name,bcbs_sbcid&$filter=(_bcbs_groupnameid_value eq ${accountId} and bcbs_planid eq '${planId}')&$orderby=createdon desc`);
        const sbcs = await this.crm.query(`bcbs_sbcs?$select=bcbs_name,bcbs_sbcid&$expand=bcbs_group_name($select=bcbs_effectivedate)&$filter=(_bcbs_groupnameid_value eq ${accountId} and bcbs_planid eq '${planId}') and (bcbs_group_name/bcbs_groupconfigurationsnapshotid ne null)&$orderby=bcbs_effectivedate desc, modifiedon desc`) as IQuery_bcbs_sbc[]

        return sbcs;
    }

    // https://dev.azure.com/blue-infosys/Group%20Library/_sprints/taskboard/Group%20Library%20Team/Group%20Library/Sprint%2021?workitem=436940
    // On Submit, if there are any related SBCs which:
    // Record State (statecode): Active
    // Status (bcbs_sbctype): Anything other than Ready
    // Manually Edited PDF (bcbs_manuallyeditedpdf): Not Yes         
    async getSBCsNotReady(bcbs_groupconfigurationsnapshotid: string) {

        // Mapping :
        // statecode, statecodename
        // 0,Active
        // 1,Inactive

        // bcbs_sbctype, bcbs_sbctypename
        // NULL,NULL
        // 1,In-Progress
        // 2,Final
        // 4,Failed to Process
        // 6,Ready

        // bcbs_manuallyeditedpdf,bcbs_manuallyeditedpdfname
        // NULL,NULL
        // 1,No
        // 2,Yes

        const sbcs = await this.crm.query(`bcbs_sbcs?$select=bcbs_name,bcbs_sbcid,_bcbs_group_name_value&$filter=(bcbs_group_name/bcbs_groupconfigurationsnapshotid eq ${bcbs_groupconfigurationsnapshotid}) and statecode eq 0 and bcbs_sbctype ne 6 and bcbs_manuallyeditedpdf ne 2&$orderby=bcbs_effectivedate desc, modifiedon desc`) as IQuery_bcbs_sbc[]
        return sbcs;
    }


    async bcbs_sbc_load(bcbs_sbcId: string): Promise<IXrmTypes.bcbs_sbc> {
        return await this.crm.getRecord(`bcbs_sbcs`, bcbs_sbcId);//need to return bcbs_sbctype as well
    }


    async fetchOpenRevisionTasks(revisionTasks: FAM.IEntityReferenceValue[], effectiveDate: string, _bcbs_groupid_value: string) {
        /*
        bcbs_statuscode:
        808900001	Assigned
        808900004	Blocked
        808900008	Cancelled (Deactivated)
        808900006	Completed
        808900002	In Progress
        808900009	No Action Required
        808900007	Not Applicable
        808900000	Not Assigned
        808900005	Pending Billing Cycle
        808900003	Pending Issue
        */

        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/414025
        // A) Related Group is the same Group as current Group Configuration Snapshot
        // B) Not in the state of Complete, Not Applicable, No Action, Cancelled, or related to the current Group Configuration Snapshot as a revision task.
        // C) Effective Date of the Group Configuration Snapshot related to the task is the same as the effective date of the current Group Configuration Snapshot

        const taskConditionIds: string[] = [];
        revisionTasks.forEach(t => taskConditionIds.push(`<value>${t.id}</value>`));
        let taskCondition = '';

        if (taskConditionIds.length > 0)
            taskCondition = `<condition attribute="activityid" operator="not-in">` + taskConditionIds.join("") + `</condition>`;

        let bcbs_statuscode_condition = ''
            + `<condition attribute="bcbs_statuscode" operator="not-in" >`
            + `<value>808900006</value>` // Complete
            + `<value>808900007</value>` // Not Applicable
            + `<value>808900009</value>` // NoActionRequired
            + `<value>808900008</value>` // Cancelled (Deactivated)
            + `</condition>`;

        const customFetch = '<fetch count="5">'
            + `<entity name="task">`
            + `<attribute name="activitytypecode" />`
            + `<attribute name="subject" />`
            + `<link-entity name="bcbs_groupconfigurationsnapshot" to="bcbs_groupconfigurationsnapshotid" from="bcbs_groupconfigurationsnapshotid" alias="bcbs_groupconfigurationsnapshot" link-type="inner">`
            + `<filter>`
            + `<condition attribute="bcbs_effectivedate" operator="eq" value="${effectiveDate}" />`
            + `<condition attribute="bcbs_groupid" operator="eq" value="${_bcbs_groupid_value}" />`
            + `</filter>`
            + `</link-entity>`
            + `<filter>`
            + `<condition attribute="bcbs_sequencenumber" operator="eq" value="02.07" />`
            + bcbs_statuscode_condition
            + taskCondition
            + `</filter>`
            + `</entity>`
            + `</fetch>`;

        const result = await this.crm.fetch<IFetchOpenRevisionTasksResponse>('tasks', customFetch, { includeAnnotations: true });
        return result;

    }

    async generateChecklistSendNotification({ bcbs_groupconfigurationsnapshotid, bcbs_name }: { bcbs_groupconfigurationsnapshotid?: string, bcbs_name?: string }) {
        const response = await this.net.execute('POST',
            'api/AzureFunction/GenerateChecklistSendNotification',
            {
                bcbs_groupconfigurationsnapshotid: bcbs_groupconfigurationsnapshotid,
                bcbs_name: bcbs_name
            }
        );
    }

    async getContacts(ids: string[]) {

        type TContact = { contactid: string, fullname: string, emailaddress1: string };
        const batchSize = 500;
        const result: Record<string, TContact> = {};
        while (ids.length > 0) {
            const batch = ids.splice(0, batchSize);
            const query = `contacts?$select=contactid,fullname,emailaddress1&$filter=${batch.map(id => `contactid eq ${id}`).join(` or `)}`;
            const results = await this.crm.queryBatch(query);
            results.forEach(element => {
                result[element.contactid] = element;
            });
        }

        return result
    }

    async saveBenefitSummary(acountId: string, tempId: string, json: FAM.IGroupConfigurationData,) {

        try {
            let blobUploadResult: IBlobUploadResult | null = null

            const blob = await this.convertObjectToZipBlob(json);
            const blobName = `account/${acountId}/benefitsummaryreport/${tempId}/snapshot.json.zip`;

            blobUploadResult = await this.blob.uploadBlob(
                {
                    blobName: blobName,
                    blob: blob
                },
                {
                    accountId: acountId,
                },
                undefined,
                undefined,
                "temp"
            );

            console.log("[appi] AppDataSource.SaveBenefitSummary saveInfo", json);

            return {
                blobName: blobName,
            }
        }
        catch (error) {
            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });
        }
        finally {

        }
    }

}